/**
 * Centralized authentication/session helpers.
 *
 * This module is the single place that reads/writes session data. Previously
 * this logic (token lookup, "requester" id resolution, clearing on logout)
 * was duplicated across axiosInstance.ts, PrivateRoute.tsx, mediaApi.ts and
 * MediaLibrary.tsx, which made it easy for the implementations to drift.
 *
 * SECURITY NOTE: tokens are kept in sessionStorage, which — like
 * localStorage — is readable by any JS running on the page (XSS risk isn't
 * eliminated by this alone). What sessionStorage changes: it is scoped to
 * the browser tab and is cleared automatically when that tab/window closes,
 * so a token can't sit around indefinitely in browser storage the way a
 * localStorage value would (this codebase previously used localStorage,
 * which never expired client-side on its own). Combined with
 * `hooks/useIdleLogout.ts`'s inactivity timeout, a stolen token now has two
 * independent bounds on how long it stays useful instead of one. This is
 * still a mitigation, not the fix — the actual fix is moving to an httpOnly
 * cookie issued by the backend, so client-side JS can't read the token at
 * all. Because all reads/writes now go through this module, migrating to
 * cookie-based sessions later only requires changing the implementation
 * below (e.g. `getToken` can become a no-op once the browser sends the
 * cookie automatically, and the backend would need to set/clear the cookie
 * on login/401 instead of the client doing so here).
 */

const TOKEN_KEY = 'admin_token';
const USER_KEY = 'admin_user';
const REQUESTER_KEY = 'requester';

export type AdminUser = Record<string, any>;

function safeGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // storage may be unavailable (private mode, quota, SSR) - ignore
  }
}

function safeRemove(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export function getToken(): string {
  const t = safeGet(TOKEN_KEY);
  if (t) return t;
  try {
    const runtime = typeof window !== 'undefined' ? window.__APP_CONFIG__ : undefined;
    return runtime?.AUTH_TOKEN || '';
  } catch {
    return '';
  }
}

export function getUser(): AdminUser | null {
  const raw = safeGet(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Resolves a stable identifier for the current admin user, used by the API
 * to attribute changes ("requester"). Falls back to 'admin' if no better
 * identifier can be found.
 */
export function getRequesterId(): string {
  const cached = safeGet(REQUESTER_KEY);
  if (cached && cached.trim() && cached !== 'admin') return cached.trim();

  const u = getUser();
  if (!u) return 'admin';

  const uid =
    u.id ??
    u.user_id ??
    u.ID ??
    u.userId ??
    u.userID ??
    u.userid ??
    u.uid ??
    (u.user &&
      (u.user.id ??
        u.user.user_id ??
        u.user.ID ??
        u.user.userId ??
        u.user.userID ??
        u.user.userid ??
        u.user.uid));

  if (uid !== undefined && uid !== null && uid !== '') {
    const val = String(uid);
    safeSet(REQUESTER_KEY, val);
    return val;
  }

  const fallback = u.email || u.name;
  return fallback ? String(fallback) : 'admin';
}

export function isAuthenticated(): boolean {
  return !!getToken();
}

/** Returns true if there is enough info to consider the session valid
 * (used by route guards). */
export function hasValidSession(): boolean {
  if (!getToken()) return false;
  const requester = safeGet(REQUESTER_KEY);
  const validRequester = !!requester && requester !== 'admin' && requester !== '';
  return validRequester || !!getUser();
}

export function setSession(token: string, user: AdminUser | null, requesterId?: string): void {
  if (token) safeSet(TOKEN_KEY, token);
  if (user) safeSet(USER_KEY, JSON.stringify(user));
  if (requesterId) {
    safeSet(REQUESTER_KEY, String(requesterId));
  } else {
    safeRemove(REQUESTER_KEY);
  }
}

export function clearSession(): void {
  safeRemove(TOKEN_KEY);
  safeRemove(USER_KEY);
  safeRemove(REQUESTER_KEY);
}
